import { useState } from 'react'
import { api, errorText } from '../api/client'
import type { Subject } from '../api/types'
import { CALL_OUTCOMES } from '../lib/constants'
import { Icon } from './Icon'
import { useToast } from './Toast'

/** Log a note or a call against any subject. ⌘/Ctrl+Enter saves. */
export function Composer({ subject, onLogged }: { subject: Subject; onLogged: () => void }) {
  const [mode, setMode] = useState<'note' | 'call'>('note')
  const [body, setBody] = useState('')
  const [outcome, setOutcome] = useState<string>('connected')
  const [busy, setBusy] = useState(false)
  const toast = useToast()

  const canSave = mode === 'call' || body.trim().length > 0

  async function save() {
    if (!canSave || busy) return
    setBusy(true)
    try {
      await api.logActivity(subject, {
        kind: mode,
        body: body.trim() || undefined,
        outcome: mode === 'call' ? outcome : undefined,
      })
      toast(mode === 'note' ? 'Note saved' : 'Call logged', { tone: 'positive' })
      setBody('')
      onLogged()
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="composer">
      <textarea
        className="field"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={mode === 'note' ? 'Write a note…' : 'What was discussed? (optional)'}
        aria-label={mode === 'note' ? 'Note' : 'Call summary'}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) save()
        }}
      />
      <div className="composer-foot">
        <div className="seg" role="tablist" aria-label="Entry type">
          <button className={mode === 'note' ? 'active' : ''} onClick={() => setMode('note')}
            role="tab" aria-selected={mode === 'note'} title="Note">
            <Icon name="edit_note" size={17} />
          </button>
          <button className={mode === 'call' ? 'active' : ''} onClick={() => setMode('call')}
            role="tab" aria-selected={mode === 'call'} title="Log a call">
            <Icon name="call" size={17} />
          </button>
        </div>
        {mode === 'call' ? (
          <select className="field" style={{ width: 190 }} value={outcome}
            onChange={(e) => setOutcome(e.target.value)} aria-label="Call outcome">
            {CALL_OUTCOMES.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        ) : null}
        <span className="spacer" />
        <button className="btn primary" onClick={save} disabled={!canSave || busy}>
          {busy ? 'Saving…' : mode === 'note' ? 'Save note' : 'Log call'}
        </button>
      </div>
    </div>
  )
}
